/**
 * career-bench-grading — the RLHF operator loop's first consumer (DEV only, route
 * `/dev/grading`, gated on `session.devMode`).
 *
 * Loads a model-benchmark run from the dev BFF and drives an operator grading pass
 * with the reusable `@platform/rlhf-elements`: (A) the contested samples — each
 * letter in an `<rlhf-annotated-text>` plus one `<rlhf-dimension-grader>` per
 * contested dimension showing both judges' verdicts; (B) a round-robin
 * `<rlhf-pairwise>` over the fx-mittelstand-sie letters; (C) the canon §5 taste
 * questions as ruling cards. Every mark/grade/preference/ruling is an event in the
 * host-provided SessionStore, persisted to the results dir through the BFF; the
 * `<rlhf-session-bar>` closes the session (writing the OKF feedback bundle).
 *
 * The D1–D9 rubric is loaded from the quality canon at RUNTIME (BFF-side); the
 * surface never mirrors it. German chrome throughout (dev tool — literal German is
 * acceptable here; the LLM-authored letter bodies render `lang="de"`).
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { html, nothing, repeat, type TemplateResult } from "@platform/web-kit/render"
import {
  createRlhfSessionStore,
  createLocalDraftStore,
  RlhfSessionStoreToken,
  RlhfDraftStoreToken,
  seamAutoGrow,
  type RlhfSessionStoreImpl,
  type LocalDraftStore,
} from "@platform/rlhf-elements"
import type { JudgeVerdictRef, RubricDimension, SessionEvent, SessionState } from "@platform/rlhf-core"
import { I18nToken, SessionToken, apiGet, apiPost } from "../services"
import type { CareerI18n } from "../../i18n"
import type { ShellSession } from "../domain"

/** The gate vocabulary offered in the annotate form (mirrors the production QualityGate). */
const GATES = ["evidence", "role-fit", "motivation", "german-clarity", "anti-slop", "reader-test", "output-hygiene", "none"]

/** The bench fixture the direct comparison runs over (canon: konservativer Mittelstand, Sie). */
const PAIRWISE_FIXTURE = "fx-mittelstand-sie"

/** Canon §5 open taste questions (F1–F5) — a typed mirror of the question stems + recommendations. */
const STIMMFRAGEN: readonly { id: string; frage: string; empfehlung: string }[] = [
  { id: "F1", frage: "Wie kühn darf die Eröffnung sein?", empfehlung: "Kühnheit als Funktion des Arbeitgeber-Registers steuern — konservativ = sachlicher Aufhänger, Startup/Tech = pointierter." },
  { id: "F2", frage: "Wie viel Persönlichkeit vs. Sachlichkeit?", empfehlung: "Persönlichkeit = ein begründeter Standpunkt + konsistente analytische Haltung, nicht Small-Talk. Sachlichkeit bleibt Grundton." },
  { id: "F3", frage: "Quereinstieg-Rahmung?", empfehlung: "Übertragbarkeit zeigen (belegtes Angrenzendes) + Lücke in einem Nebensatz ehrlich benennen — nie beschönigen, nie überbetonen." },
  { id: "F4", frage: "Die »Beleg«-Vokabel im nutzersichtbaren Text?", empfehlung: "Im UI »Belege« lassen; in der Bewerbungs-Prosa das Substantiv meiden und die Verbform nutzen (»belegt durch«, »gezeigt an«)." },
  { id: "F5", frage: "Gewichtung & Gesamtscore?", empfehlung: "Kein einzelner Score vor Operator-Kalibrierung — erst 20–30 Bewerbungen benoten, Judge-vs-Operator-Übereinstimmung pro Dimension messen." },
]

// ─── Raw payload shapes (as the BFF serves them) ─────────────────────────────────

interface GenerationRecord {
  fixtureId: string
  modelId: string
  modelLabel: string
  letterBody: string
  degraded: boolean
}
interface DimensionVerdict {
  dimension: string
  score: number
  rationale: string
  quote?: string
}
interface JudgeVerdict {
  judgeModel: string
  dimensions: DimensionVerdict[]
}
interface PanelResult {
  fixtureId: string
  sampleModelId: string
  verdicts: JudgeVerdict[]
  contestedDimensions: string[]
  contested: boolean
}
interface BenchPayload {
  label: string
  manifest: { generations: GenerationRecord[] }
  judges: PanelResult[]
  rubric: RubricDimension[]
}

// ─── Derived view shapes ─────────────────────────────────────────────────────────

interface ContestedCase {
  subjectRef: string
  fixtureId: string
  modelLabel: string
  letterBody: string
  dimensions: { dimension: RubricDimension; judges: JudgeVerdictRef[] }[]
}
interface PairwisePair {
  leftRef: string
  rightRef: string
  leftLabel: string
  rightLabel: string
  leftText: string
  rightText: string
}

const subjectRef = (fixtureId: string, modelId: string): string => `${fixtureId}::${modelId}`

export class BenchGradingVm implements ViewModel {
  readonly bench: Signal<BenchPayload | null> = signal<BenchPayload | null>(null)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly saveError: Signal<string | null> = signal<string | null>(null)
  readonly loaded: Signal<boolean> = signal(false)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly store: RlhfSessionStoreImpl
  /** Pre-save seamless-input text (Begründungen, ruling text) that survives a reload. */
  readonly drafts: LocalDraftStore
  readonly sessionId = "grading-session-live"
  private readonly state: ReadonlySignal<SessionState>

  constructor(
    private readonly benchLabel: string,
    readonly t: CareerI18n["t"],
    readonly actor: string,
  ) {
    this.store = createRlhfSessionStore({ onAppend: (_event, log) => void this.persist(log) })
    this.drafts = createLocalDraftStore({ sessionId: this.sessionId })
    this.state = this.store.state()
    this.renderDeps = [this.bench, this.error, this.saveError, this.loaded, this.state]
  }

  attach(): () => void {
    void this.load()
    // Reliability: flush any debounced drafts the instant the tab is hidden/closed so a
    // reload never loses the last few keystrokes (the debounce window would otherwise drop them).
    const flush = (): void => this.drafts.flush()
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", flush)
    if (typeof window !== "undefined") window.addEventListener("pagehide", flush)
    return () => {
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", flush)
      if (typeof window !== "undefined") window.removeEventListener("pagehide", flush)
      this.drafts.dispose()
    }
  }

  private async load(): Promise<void> {
    try {
      const bench = await apiGet<BenchPayload>(`/api/dev/bench/${this.benchLabel}`)
      // REHYDRATION: seed the session store from the persisted event log BEFORE the
      // grading elements render/connect — they prefill from the folded store state in
      // attach(), so the log must be in place before bench.set() mounts them. A missing
      // or unreadable log is a fresh session, never a hard failure (grading must proceed).
      try {
        const persisted = await apiGet<{ events?: SessionEvent[] }>(
          `/api/dev/bench/${this.benchLabel}/grading-session?sessionId=${encodeURIComponent(this.sessionId)}`,
        )
        if (persisted.events && persisted.events.length > 0) this.store.hydrate(persisted.events)
      } catch {
        // no prior session for this run — start clean
      }
      this.bench.set(bench)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loaded.set(true)
    }
  }

  /** Persist the full event log to the results dir through the BFF (fire-and-forget). */
  private async persist(log: readonly SessionEvent[]): Promise<void> {
    try {
      await apiPost(`/api/dev/bench/${this.benchLabel}/grading-session`, { sessionId: this.sessionId, events: log })
      this.saveError.set(null)
    } catch (e) {
      this.saveError.set(e instanceof Error ? e.message : String(e))
    }
  }

  /** Force-write the OKF feedback bundle from the current log (the export button). */
  async exportBundle(): Promise<void> {
    try {
      await apiPost(`/api/dev/bench/${this.benchLabel}/grading-session?export=1`, {
        sessionId: this.sessionId,
        events: this.store.events(),
      })
      this.saveError.set(null)
    } catch (e) {
      this.saveError.set(e instanceof Error ? e.message : String(e))
    }
  }

  currentState(): SessionState {
    return this.state.value
  }

  rubricById(id: string): RubricDimension | undefined {
    return this.bench.value?.rubric.find((d) => d.id === id)
  }

  /** The judges' scores for one dimension (one per judge model, for calibration display). */
  private judgeRefsFor(panel: PanelResult, dim: string): JudgeVerdictRef[] {
    const byModel = new Map<string, JudgeVerdictRef>()
    for (const verdict of panel.verdicts) {
      const entry = verdict.dimensions.find((d) => d.dimension === dim)
      if (entry && !byModel.has(verdict.judgeModel)) {
        byModel.set(verdict.judgeModel, {
          judgeModel: verdict.judgeModel,
          dimension: dim,
          score: entry.score,
          rationale: entry.rationale,
          ...(entry.quote ? { quote: entry.quote } : {}),
        })
      }
    }
    return [...byModel.values()]
  }

  /** The contested samples, joined to their letters + per-dimension judge verdicts. */
  contestedCases(): ContestedCase[] {
    const bench = this.bench.value
    if (!bench) return []
    const genOf = (fixtureId: string, modelId: string): GenerationRecord | undefined =>
      bench.manifest.generations.find((g) => g.fixtureId === fixtureId && g.modelId === modelId)
    return bench.judges
      .filter((p) => p.contested && p.contestedDimensions.length > 0)
      .map((panel) => {
        const gen = genOf(panel.fixtureId, panel.sampleModelId)
        const dimensions = panel.contestedDimensions
          .map((dim) => ({ dimension: this.rubricById(dim), judges: this.judgeRefsFor(panel, dim) }))
          .filter((d): d is { dimension: RubricDimension; judges: JudgeVerdictRef[] } => d.dimension !== undefined)
        return {
          subjectRef: subjectRef(panel.fixtureId, panel.sampleModelId),
          fixtureId: panel.fixtureId,
          modelLabel: gen?.modelLabel ?? panel.sampleModelId,
          letterBody: gen?.letterBody ?? "",
          dimensions,
        }
      })
      .filter((c) => c.letterBody.length > 0 && c.dimensions.length > 0)
  }

  /** Round-robin pairs over the non-degraded fx-mittelstand-sie letters. */
  pairwisePairs(): PairwisePair[] {
    const bench = this.bench.value
    if (!bench) return []
    const gens = bench.manifest.generations.filter((g) => g.fixtureId === PAIRWISE_FIXTURE && !g.degraded && g.letterBody)
    const pairs: PairwisePair[] = []
    for (let i = 0; i < gens.length; i++) {
      for (let j = i + 1; j < gens.length; j++) {
        const a = gens[i]!
        const b = gens[j]!
        pairs.push({
          leftRef: subjectRef(a.fixtureId, a.modelId),
          rightRef: subjectRef(b.fixtureId, b.modelId),
          leftLabel: a.modelLabel,
          rightLabel: b.modelLabel,
          leftText: a.letterBody,
          rightText: b.letterBody,
        })
      }
    }
    return pairs
  }

  /** Total gradings expected (one per contested dimension) — the session-bar denominator. */
  expectedGradings(): number {
    return this.contestedCases().reduce((n, c) => n + c.dimensions.length, 0)
  }

  recordRuling(questionId: string, ruling: string, notes: string): void {
    const value = ruling.trim()
    if (!value) return
    this.store.append({
      type: "ruling.Recorded",
      at: new Date().toISOString(),
      actor: this.actor,
      ruling: { questionId, ruling: value, ...(notes.trim() ? { notes: notes.trim() } : {}) },
    })
    // Saved — the durable ruling event supersedes the pre-save drafts.
    this.drafts.clear(rulingDraftField(questionId))
    this.drafts.clear(rulingNotesField(questionId))
  }

  rulingFor(questionId: string): string | undefined {
    return this.currentState().rulings.find((r) => r.questionId === questionId)?.ruling
  }

  notesFor(questionId: string): string | undefined {
    return this.currentState().rulings.find((r) => r.questionId === questionId)?.notes
  }

  /** The value a ruling's decision field should show: the saved ruling, else a restored draft. */
  rulingValue(questionId: string): string {
    return this.rulingFor(questionId) ?? this.drafts.read(rulingDraftField(questionId)) ?? ""
  }
  /** The value a ruling's notes field should show: the saved notes, else a restored draft. */
  notesValue(questionId: string): string {
    return this.notesFor(questionId) ?? this.drafts.read(rulingNotesField(questionId)) ?? ""
  }
  /** True when an unsaved draft for this ruling was restored (no saved ruling yet). */
  rulingDraftRestored(questionId: string): boolean {
    if (this.rulingFor(questionId)) return false
    return this.drafts.read(rulingDraftField(questionId)) !== null || this.drafts.read(rulingNotesField(questionId)) !== null
  }
  writeRulingDraft(questionId: string, value: string): void {
    this.drafts.write(rulingDraftField(questionId), value)
  }
  writeNotesDraft(questionId: string, value: string): void {
    this.drafts.write(rulingNotesField(questionId), value)
  }
}

/** The per-question keys the unsaved ruling decision / notes are buffered under. */
const rulingDraftField = (questionId: string): string => `ruling:${questionId}:decision`
const rulingNotesField = (questionId: string): string => `ruling:${questionId}:notes`

export class CareerBenchGrading extends KitElement<BenchGradingVm> {
  // The <rlhf-session-bar> "Exportieren" click dispatches this composed event; the
  // surface forces an OKF-bundle write (close writes it too, via the store append).
  readonly #onExport = (): void => void this.vm.exportBundle()

  override connectedCallback(): void {
    super.connectedCallback()
    this.addEventListener("rlhf-session-bar:export", this.#onExport)
  }

  override disconnectedCallback(): void {
    this.removeEventListener("rlhf-session-bar:export", this.#onExport)
    super.disconnectedCallback()
  }

  protected createViewModel(ctx: KitContext): BenchGradingVm {
    const i18n = ctx.inject(I18nToken)
    const session = ctx.inject(SessionToken) as ShellSession
    const actor = session.user?.name ?? session.user?.sub ?? "operator"
    const vm = new BenchGradingVm("latest", i18n.t, actor)
    // Provide the SessionStore + DraftStore to the rlhf-* children BEFORE they connect
    // (they inject them). createViewModel runs during #bind, before the first render flush.
    ctx.provide(RlhfSessionStoreToken, vm.store)
    ctx.provide(RlhfDraftStoreToken, vm.drafts)
    return vm
  }

  #contestedSection(vm: BenchGradingVm): TemplateResult {
    const cases = vm.contestedCases()
    return html`
      <section class="grading-section" aria-labelledby="grading-contested-title">
        <h2 id="grading-contested-title" class="surface-section-title" lang="de">A · Strittige Fälle</h2>
        <p class="grading-lead" lang="de">
          Die Judges sind sich uneins (&gt;±1). Lies den Brief, benote die strittige Dimension gegen die
          Canon-Anker — die Urteile beider Judges stehen daneben.
        </p>
        ${cases.length === 0
          ? html`<p class="grading-empty" lang="de">Keine strittigen Fälle in diesem Lauf.</p>`
          : repeat(
              cases,
              (c) => c.subjectRef,
              (c) => html`
                <article class="grading-case" data-subject=${c.subjectRef}>
                  <header class="grading-case-head">
                    <span class="grading-chip grading-chip-fixture" lang="de">${c.fixtureId}</span>
                    <span class="grading-chip grading-chip-model" lang="de">${c.modelLabel}</span>
                  </header>
                  <rlhf-annotated-text
                    .text=${c.letterBody}
                    .subjectRef=${c.subjectRef}
                    .gates=${GATES}
                    .actor=${vm.actor}
                  ></rlhf-annotated-text>
                  <div class="grading-dimensions">
                    ${repeat(
                      c.dimensions,
                      (d) => d.dimension.id,
                      (d) => html`<rlhf-dimension-grader
                        .dimension=${d.dimension}
                        .subjectRef=${c.subjectRef}
                        .judges=${d.judges}
                        .revealJudges=${true}
                        .actor=${vm.actor}
                      ></rlhf-dimension-grader>`,
                    )}
                  </div>
                </article>
              `,
            )}
      </section>
    `
  }

  #pairwiseSection(vm: BenchGradingVm): TemplateResult {
    const pairs = vm.pairwisePairs()
    return html`
      <section class="grading-section" aria-labelledby="grading-pairwise-title">
        <h2 id="grading-pairwise-title" class="surface-section-title" lang="de">B · Direktvergleich (${PAIRWISE_FIXTURE})</h2>
        <p class="grading-lead" lang="de">
          Gleicher Kandidat, gleiche Stelle — welcher Brief würde eingeladen? Wähle je Paar den besseren.
        </p>
        ${pairs.length === 0
          ? html`<p class="grading-empty" lang="de">Kein vergleichbares Modell-Paar für diese Stelle.</p>`
          : repeat(
              pairs,
              (p) => `${p.leftRef}|${p.rightRef}`,
              (p) => html`<div class="grading-pair">
                <p class="grading-pair-heads" lang="de">${p.leftLabel} vs. ${p.rightLabel}</p>
                <rlhf-pairwise
                  .leftRef=${p.leftRef}
                  .rightRef=${p.rightRef}
                  .leftText=${p.leftText}
                  .rightText=${p.rightText}
                  .actor=${vm.actor}
                ></rlhf-pairwise>
              </div>`,
            )}
      </section>
    `
  }

  #stimmfragenSection(vm: BenchGradingVm): TemplateResult {
    return html`
      <section class="grading-section" aria-labelledby="grading-stimmfragen-title">
        <h2 id="grading-stimmfragen-title" class="surface-section-title" lang="de">C · Stimmfragen (Canon §5)</h2>
        <p class="grading-lead" lang="de">Genuin offene Geschmacksfragen — entscheide und begründe kurz.</p>
        <ul class="grading-rulings" role="list">
          ${STIMMFRAGEN.map((q) => {
            const recorded = vm.rulingFor(q.id)
            return html`<li class="grading-ruling" data-question=${q.id}>
              <h3 class="grading-ruling-frage" lang="de">${q.id} — ${q.frage}</h3>
              <p class="grading-ruling-empfehlung" lang="de">Empfehlung: ${q.empfehlung}</p>
              ${recorded ? html`<p class="grading-ruling-recorded" role="status" lang="de">Entschieden: ${recorded}</p>` : nothing}
              <label class="rlhf-field" lang="de">
                <span class="rlhf-field-label" lang="de">Entscheidung</span>
                <textarea
                  id=${`ruling-${q.id}`}
                  class="rlhf-seam"
                  rows="1"
                  ${seamAutoGrow()}
                  placeholder="Deine Entscheidung — entschieden und begründet."
                  .value=${vm.rulingValue(q.id)}
                  @input=${(e: Event) => vm.writeRulingDraft(q.id, (e.target as HTMLTextAreaElement).value)}
                  lang="de"
                ></textarea>
                ${vm.rulingDraftRestored(q.id)
                  ? html`<span class="rlhf-draft-hint" role="status" lang="de">Entwurf wiederhergestellt</span>`
                  : nothing}
              </label>
              <label class="rlhf-field" lang="de">
                <span class="rlhf-field-label" lang="de">Notiz (optional)</span>
                <textarea
                  id=${`ruling-notes-${q.id}`}
                  class="rlhf-seam"
                  rows="1"
                  ${seamAutoGrow()}
                  placeholder="Zusatz, Ausnahme, offener Punkt …"
                  .value=${vm.notesValue(q.id)}
                  @input=${(e: Event) => vm.writeNotesDraft(q.id, (e.target as HTMLTextAreaElement).value)}
                  lang="de"
                ></textarea>
              </label>
              <div class="grading-ruling-actions">
                <button type="button" class="rlhf-btn rlhf-btn-quiet grading-ruling-save" data-act="record-ruling" data-question=${q.id} lang="de">
                  Entscheidung festhalten
                </button>
              </div>
            </li>`
          })}
        </ul>
      </section>
    `
  }

  protected render(vm: BenchGradingVm): TemplateResult {
    const bench = vm.bench.value
    if (!bench) {
      return html`
        <section class="surface bench-grading" aria-labelledby="grading-title">
          <h1 id="grading-title" class="surface-title" lang="de">Bench-Bewertung</h1>
          ${vm.error.value
            ? html`<p class="surface-error" role="alert" lang="de">Laden fehlgeschlagen: ${vm.error.value}</p>`
            : vm.loaded.value
              ? html`<p class="surface-empty" lang="de">Kein Benchmark-Lauf gefunden.</p>`
              : html`<p class="grading-loading" aria-busy="true" lang="de">Lade Benchmark-Lauf …</p>`}
        </section>
      `
    }
    return html`
      <section class="surface bench-grading" aria-labelledby="grading-title">
        <h1 id="grading-title" class="surface-title" lang="de">Bench-Bewertung — ${bench.label}</h1>
        ${vm.saveError.value
          ? html`<p class="surface-error" role="alert" lang="de">Speichern fehlgeschlagen: ${vm.saveError.value}</p>`
          : nothing}
        <rlhf-session-bar
          .total=${vm.expectedGradings()}
          .label=${bench.label}
          .actor=${vm.actor}
        ></rlhf-session-bar>
        ${this.#contestedSection(vm)}
        ${this.#pairwiseSection(vm)}
        ${this.#stimmfragenSection(vm)}
      </section>
    `
  }

  protected override actions(vm: BenchGradingVm): ActionMap {
    return {
      "record-ruling": ({ question }) => {
        if (!question) return
        const ruling = (this.querySelector(`#ruling-${question}`) as HTMLTextAreaElement | null)?.value ?? ""
        const notes = (this.querySelector(`#ruling-notes-${question}`) as HTMLTextAreaElement | null)?.value ?? ""
        vm.recordRuling(question, ruling, notes)
      },
    }
  }
}
